import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import type { RootState } from "../../../store/store";

import { listEmployeesApi, DpEmployeeListItem } from "../api/employeesApi";
import { listCostCentersApi, DpCostCenter } from "../api/settingsApi";
import { DpEmployeeStatus, EMPLOYEE_STATUS_LABELS } from "../api/dpTypes";
import { formatCpf, orDash } from "../utils/format";
import { DP_EMPLOYEE_NEW_PAGE, DP_EMPLOYEE_PAGE, DP_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./DpEco.css";

const LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 350;

const PARTICULAS = new Set(["da", "de", "do", "das", "dos", "e"]);

/** "Ana Beatriz de Souza" → "AS" (primeiro e último nome, sem partículas). */
const initialsOf = (name: string) => {
  const parts = name.trim().split(/\s+/).filter((p) => p && !PARTICULAS.has(p.toLowerCase()));
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

const StatusPill: React.FC<{ status: DpEmployeeStatus }> = ({ status }) => (
  <span className={`dpeStatus dpeStatus--${status}`}>{EMPLOYEE_STATUS_LABELS[status] ?? status}</span>
);

export default function EmployeesPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [items, setItems] = useState<DpEmployeeListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // `search` é o que o operador digita; `q` é o que vai para o servidor depois de
  // uma pausa curta — a busca é server-side, então não vale uma requisição por tecla.
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<DpEmployeeStatus | "">("");
  const [costCenterId, setCostCenterId] = useState("");
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await listEmployeesApi(token, {
        page,
        limit: LIMIT,
        q: q || undefined,
        status: status || undefined,
        costCenterId: costCenterId || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao listar colaboradores.");
    } finally {
      setLoading(false);
    }
  }, [token, page, q, status, costCenterId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    listCostCentersApi(token)
      .then(setCostCenters)
      .catch(() => setCostCenters([]));
  }, [token]);

  const clearFilters = () => {
    setSearch("");
    setQ("");
    setStatus("");
    setCostCenterId("");
    setPage(1);
  };

  if (!token) return null;

  const hasFilters = Boolean(search || status || costCenterId);
  const countLabel = `${total} ${total === 1 ? "colaborador" : "colaboradores"}${
    status ? ` com situação "${EMPLOYEE_STATUS_LABELS[status].toLowerCase()}"` : ""
  }`;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate("/") },
            { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
            { label: "Colaboradores" },
          ]}
          title="Colaboradores"
          subtitle="Pessoas contratadas pela CLT, com contrato, dependentes e documentos."
          onBack={goBack}
          backLabel="Voltar para o DP"
          actions={
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(DP_EMPLOYEE_NEW_PAGE())}>
              <EcoIcon name="plus" size={18} />
              Novo colaborador
            </button>
          }
        />

        <section className="ecoCard dpeList" aria-label="Lista de colaboradores">
          <div className="dpeList__toolbar">
            <div className="dpeList__filters">
              <label htmlFor="dp-busca" className="ecoVisuallyHidden">Buscar por nome ou CPF</label>
              <div className="ecoSearch dpeList__search">
                <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                <input
                  id="dp-busca"
                  type="search"
                  className="ecoInput ecoInput--sm"
                  placeholder="Buscar por nome ou CPF"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <label htmlFor="dp-status" className="ecoVisuallyHidden">Situação</label>
              <select
                id="dp-status"
                className="ecoSelect ecoSelect--sm dpeList__select"
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value as DpEmployeeStatus | "");
                }}
              >
                <option value="">Todas as situações</option>
                {(Object.keys(EMPLOYEE_STATUS_LABELS) as DpEmployeeStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {EMPLOYEE_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>

              <label htmlFor="dp-cc" className="ecoVisuallyHidden">Centro de custo</label>
              <select
                id="dp-cc"
                className="ecoSelect ecoSelect--sm dpeList__select"
                value={costCenterId}
                onChange={(e) => {
                  setPage(1);
                  setCostCenterId(e.target.value);
                }}
              >
                <option value="">Todos os centros de custo</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.code} — {cc.name}
                  </option>
                ))}
              </select>

              {hasFilters ? (
                <button type="button" className="ecoLinkBtn" onClick={clearFilters}>
                  Limpar filtros
                </button>
              ) : null}
            </div>
            <span className="dpeList__count" aria-live="polite">
              {loading ? "Carregando..." : countLabel}
            </span>
          </div>

          {!loading && items.length === 0 ? (
            <div className="dpeList__empty">
              <p className="ecoEmpty">Nenhum colaborador encontrado.</p>
            </div>
          ) : (
            <>
              <div className="ecoTableWrap dpeList__table">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th scope="col">Colaborador</th>
                      <th scope="col">CPF</th>
                      <th scope="col">Situação</th>
                      <th scope="col">Contato</th>
                      <th scope="col" className="ecoTable__num">Ação</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading}>
                    {items.map((employee) => (
                      <tr key={employee.id}>
                        <td>
                          <span className="dpePerson">
                            <span className="dpePerson__avatar" aria-hidden="true">{initialsOf(employee.fullName)}</span>
                            <span className="dpePerson__text">
                              <span className="dpePerson__name">{employee.fullName}</span>
                              {employee.socialName ? (
                                <span className="dpePerson__sub">Nome social: {employee.socialName}</span>
                              ) : null}
                            </span>
                          </span>
                        </td>
                        <td className="ecoTable__muted">{formatCpf(employee.cpf)}</td>
                        <td><StatusPill status={employee.status} /></td>
                        <td>
                          <span className="dpeContact">
                            <span>{orDash(employee.phone)}</span>
                            <span>{orDash(employee.email)}</span>
                          </span>
                        </td>
                        <td className="ecoTable__num">
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--sm"
                            onClick={() => navigate(DP_EMPLOYEE_PAGE(employee.id))}
                            aria-label={`Abrir ficha de ${employee.fullName}`}
                          >
                            Abrir ficha
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="dpeList__cards" aria-busy={loading}>
                {items.map((employee) => (
                  <li key={employee.id}>
                    <button type="button" className="dpeList__card" onClick={() => navigate(DP_EMPLOYEE_PAGE(employee.id))}>
                      <span className="dpePerson">
                        <span className="dpePerson__avatar" aria-hidden="true">{initialsOf(employee.fullName)}</span>
                        <span className="dpePerson__text">
                          <span className="dpePerson__name">{employee.fullName}</span>
                          <span className="dpePerson__sub">{formatCpf(employee.cpf)}</span>
                        </span>
                      </span>
                      {employee.status !== "ATIVO" ? <StatusPill status={employee.status} /> : null}
                      <EcoIcon name="chevronRight" className="dpeList__cardChevron" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="dpeList__pager">
            <EcoPagination page={page} pageSize={LIMIT} total={total} onChange={setPage} disabled={loading} />
          </div>
        </section>
      </main>
    </div>
  );
}
